import React from 'react';
import { MapPin, Phone, Mail, MessageCircle, Heart, Facebook, Instagram } from 'lucide-react';
import { schoolInfo } from '../data/mockData';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'About Us', id: 'about' },
    { name: 'Programs', id: 'programs' },
    { name: 'Gallery', id: 'gallery' },
    { name: 'Admissions', id: 'admissions' },
    { name: 'Contact', id: 'contact' }
  ];

  const socialLinks = [
    { 
      name: 'Facebook', 
      icon: Facebook, 
      url: 'https://www.facebook.com/schoolgyanodeepti/',
      color: 'hover:bg-blue-600' 
    },
    { 
      name: 'Instagram', 
      icon: Instagram, 
      url: 'https://www.instagram.com/gyanodeepti_school/',
      color: 'hover:bg-pink-600' 
    }
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">G</span>
              </div>
              <h3 className="text-xl font-bold">Gyanodeepti</h3>
            </div>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Nurturing young minds with quality education, care, and values in Sonipat, Haryana.
            </p>
            <div className="text-sm text-gray-400">
              <p>School & Day Care</p>
              <p>Playgroup to Class 5</p>
            </div>

            {/* Social Media Links */}
            <div className="mt-6">
              <p className="text-sm font-semibold mb-3">Follow Us</p>
              <div className="flex space-x-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 bg-gray-800 ${social.color} rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg`}
                    aria-label={social.name}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-4">Contact Info</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <a href={`tel:${schoolInfo.phone}`} className="text-gray-400 hover:text-white transition-colors">
                  {schoolInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MessageCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <a 
                  href={`https://wa.me/${schoolInfo.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  {schoolInfo.whatsapp}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <a href={`mailto:${schoolInfo.email}`} className="text-gray-400 hover:text-white transition-colors break-all">
                  {schoolInfo.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Address */}
          <div>
            <h4 className="text-lg font-bold mb-4">Visit Us</h4>
            <div className="flex items-start space-x-3 mb-4">
              <MapPin className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-gray-400 leading-relaxed">
                {schoolInfo.address}
              </p>
            </div>
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-300 mb-2">School Timings:</p>
              <p className="text-sm text-gray-400">Summer: 9:00 AM – 12:30 PM</p>
              <p className="text-sm text-gray-400">Winter: 9:30 AM – 1:00 PM</p>
              <p className="text-xs text-gray-500 mt-2">Mon - Sat</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Gyanodeepti School & Day Care. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm flex items-center">
              Made with <Heart className="w-4 h-4 text-red-500 mx-1" /> for young learners
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
